import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const zones = [
  { key: 'bins', label: 'Bins', range: 'AA–AT' },
  { key: 'tubs', label: 'Tubs', range: '01–28' },
  { key: 'trays', label: 'Trays', range: '01–26' },
  { key: 'drawers', label: 'White Drawers', range: 'WD001+' },
  { key: 'mf', label: 'Minifigs', range: 'MF01–MF19' },
  { key: 'binder', label: 'Binder', range: 'Pages 01–24 + FF' },
];

const BIN_LAYOUT = {
  64: ['AA', 'AB', 'AC', 'AM'],
  44: ['AD', 'AE', 'AF', 'AL', 'AN', 'AS'],
  24: ['AG', 'AH', 'AI', 'AJ', 'AK', 'AO', 'AP', 'AQ', 'AR', 'AT'],
};

const ALL_BIN_PREFIXES = Object.values(BIN_LAYOUT).flat().sort();

function getBinCount(prefix) {
  for (const [count, prefixes] of Object.entries(BIN_LAYOUT)) {
    if (prefixes.includes(prefix)) return Number(count);
  }
  return 0;
}

function generateBinNames(zone, binZone) {
  if (zone === 'bins') {
    if (binZone) {
      const count = getBinCount(binZone);
      return Array.from({ length: count }, (_, i) =>
        `${binZone}${String(i + 1).padStart(3, '0')}`
      );
    }
    // Return all bins (used for lot counting)
    const names = [];
    Object.entries(BIN_LAYOUT).forEach(([count, prefixes]) => {
      prefixes.forEach(prefix => {
        for (let i = 1; i <= Number(count); i++) {
          names.push(`${prefix}${String(i).padStart(3, '0')}`);
        }
      });
    });
    names.sort();
    return names;
  }
  if (zone === 'tubs') {
    return Array.from({ length: 28 }, (_, i) => `TUB ${String(i + 1).padStart(2, '0')}`);
  }
  if (zone === 'trays') {
    return Array.from({ length: 26 }, (_, i) => `TRAY ${String(i + 1).padStart(2, '0')}`);
  }
  if (zone === 'drawers') {
    return Array.from({ length: 41 }, (_, i) => `WD${String(i + 1).padStart(3, '0')}`);
  }
  if (zone === 'mf') {
    return Array.from({ length: 19 }, (_, i) => `MF${String(i + 1).padStart(2, '0')}`);
  }
  if (zone === 'binder') {
    return [
      ...Array.from({ length: 24 }, (_, i) => `BINDER ${String(i + 1).padStart(2, '0')}`),
      'BINDER FF',
    ];
  }
  return [];
}

function MoveBinPanel() {
  const [open, setOpen] = useState(false);
  const [fromBin, setFromBin] = useState('');
  const [toBin, setToBin] = useState('');
  const [moving, setMoving] = useState(false);
  const [result, setResult] = useState(null);

  async function handleMoveBin() {
    if (!fromBin.trim() || !toBin.trim()) return;
    if (fromBin.trim().toUpperCase() === toBin.trim().toUpperCase()) return;
    setMoving(true);
    setResult(null);
    const from = fromBin.trim().toUpperCase();
    const to = toBin.trim().toUpperCase();
    try {
      const res = await fetch(`${API_BASE}/api/inventories`);
      const json = await res.json();
      const lots = (json.data || []).filter(l => (l.remarks || '').toUpperCase() === from);
      if (lots.length === 0) { setResult({ error: `No lots found in ${from}` }); setMoving(false); return; }
      let moved = 0;
      for (const lot of lots) {
        try {
          await fetch(`${API_BASE}/api/inventories/${lot.inventory_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ remarks: to }),
          });
          await supabase.from('storage_history').insert({
            inventory_id: lot.inventory_id,
            part_no: lot.item?.no || '',
            color_name: lot.color_name || '',
            from_bin: from,
            to_bin: to,
            moved_at: new Date().toISOString(),
          });
          moved++;
        } catch (e) { /* continue */ }
      }
      setResult({ success: `Moved ${moved} lot${moved !== 1 ? 's' : ''} from ${from} to ${to}` });
      setFromBin('');
      setToBin('');
    } catch (err) {
      setResult({ error: err.message });
    }
    setMoving(false);
  }

  return (
    <div style={{
      background: colors.cardBg, border: `1px solid ${colors.border}`,
      borderRadius: '6px', marginBottom: '20px', overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>↔ Move Entire Bin</span>
        <span style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textSecondary }}>
            Move all lots from one bin to another. Updates BrickLink and logs to history.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>From</div>
              <input type="text" value={fromBin} onChange={e => setFromBin(e.target.value)} placeholder="e.g. AA012"
                style={{ width: '100%', padding: '10px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.95rem', fontWeight: 700, textAlign: 'center', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>To</div>
              <input type="text" value={toBin} onChange={e => setToBin(e.target.value)} placeholder="e.g. AB033"
                onKeyDown={e => { if (e.key === 'Enter') handleMoveBin(); }}
                style={{ width: '100%', padding: '10px', background: '#f9fafb', border: `1px solid ${colors.border}`, borderRadius: '6px', fontFamily: "'Outfit', sans-serif", fontSize: '0.95rem', fontWeight: 700, textAlign: 'center', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <button onClick={handleMoveBin} disabled={moving || !fromBin.trim() || !toBin.trim()}
            style={{
              padding: '12px', background: fromBin.trim() && toBin.trim() && !moving ? colors.navy : '#e2e4e8',
              color: fromBin.trim() && toBin.trim() && !moving ? '#fff' : colors.textSecondary,
              border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
              fontWeight: 800, fontSize: '0.9rem', cursor: fromBin.trim() && toBin.trim() && !moving ? 'pointer' : 'not-allowed',
            }}
          >{moving ? 'Moving...' : 'Move All Lots'}</button>
          {result?.success && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 14px', fontSize: '0.82rem', color: colors.greenDark, fontWeight: 600 }}>
              ✓ {result.success}
            </div>
          )}
          {result?.error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', fontSize: '0.82rem', color: '#dc2626' }}>
              {result.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function Stockroom() {
  const [activeZone, setActiveZone] = useState('bins');
  const [binZone, setBinZone] = useState(null);
  const [auditedBins, setAuditedBins] = useState({});
  const [lotCounts, setLotCounts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchAuditDates() {
      try {
        const { data } = await supabase
          .from('bin_audit_dates')
          .select('bin_name, last_audited');
        if (data) {
          const lookup = {};
          data.forEach(row => { lookup[row.bin_name] = row.last_audited; });
          setAuditedBins(lookup);
        }
      } catch (err) {
        console.error('Error fetching audit dates:', err);
      }
    }
    fetchAuditDates();
    async function fetchLotCounts() {
      try {
        const res = await fetch(`${API_BASE}/api/inventories`);
        const json = await res.json();
        const items = json.data || [];
        const counts = {};
        items.forEach(lot => {
          const bin = lot.remarks || '';
          if (bin) {
            counts[bin] = (counts[bin] || 0) + 1;
          }
        });
        setLotCounts(counts);
      } catch (err) {
        console.error('Error fetching lot counts:', err);
      }
    }
    fetchLotCounts();
  }, []);

  function handleZoneClick(zoneKey) {
    setActiveZone(zoneKey);
    setBinZone(null);
  }

  const currentZone = zones.find(z => z.key === activeZone);
  const showingBinPicker = activeZone === 'bins' && !binZone;
  const binNames = showingBinPicker ? [] : generateBinNames(activeZone, binZone);

  function getDensityColor(count) {
    if (!count || count === 0) return null;
    if (count <= 10) return { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' };
    if (count <= 25) return { bg: '#fffbeb', border: '#fde68a', text: '#b45309' };
    if (count <= 40) return { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' };
    return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
  }

  function formatDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Calculate totals per bin prefix for the zone picker
  function getPrefixStats(prefix) {
    const count = getBinCount(prefix);
    let totalLots = 0;
    let auditedCount = 0;
    for (let i = 1; i <= count; i++) {
      const name = `${prefix}${String(i).padStart(3, '0')}`;
      totalLots += lotCounts[name] || 0;
      if (auditedBins[name]) auditedCount++;
    }
    return { slots: count, totalLots, auditedCount };
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Stockroom" icon="▦" />

      {/* Zone Tabs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        marginBottom: '24px',
      }}>
        {zones.map(zone => {
          const isActive = activeZone === zone.key;
          return (
            <div
              key={zone.key}
              onClick={() => handleZoneClick(zone.key)}
              style={{
                background: isActive ? colors.navy : colors.cardBg,
                border: `1px solid ${isActive ? colors.navy : colors.border}`,
                borderRadius: '6px',
                padding: '14px 10px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{
                fontSize: '1rem',
                fontWeight: 700,
                color: isActive ? '#ffffff' : colors.textPrimary,
              }}>{zone.label}</div>
              <div style={{
                fontSize: '0.68rem',
                color: isActive ? '#94a3b8' : colors.textSecondary,
                marginTop: '3px',
              }}>{zone.range}</div>
            </div>
          );
        })}
      </div>

      {/* Quick links */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <div
          onClick={() => navigate('/dupes')}
          style={{
            flex: 1, background: colors.cardBg, border: `1px solid ${colors.border}`,
            borderRadius: '6px', padding: '14px', textAlign: 'center', cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: '1.2rem', marginBottom: '4px', color: colors.navy }}>⧉</div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>Duplicates</div>
          <div style={{ fontSize: '0.65rem', color: colors.textSecondary }}>Find dupes</div>
        </div>
        <div
          onClick={() => navigate('/history')}
          style={{
            flex: 1, background: colors.cardBg, border: `1px solid ${colors.border}`,
            borderRadius: '6px', padding: '14px', textAlign: 'center', cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: '1.2rem', marginBottom: '4px', color: colors.navy }}>≡</div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>History</div>
          <div style={{ fontSize: '0.65rem', color: colors.textSecondary }}>Bin moves</div>
        </div>
      </div>
      {/* Move Entire Bin */}
      <MoveBinPanel />
      {/* Bin Zone Picker (intermediate step for bins) */}
      {showingBinPicker && (
        <>
          <div style={{
            fontSize: '0.78rem',
            color: colors.textSecondary,
            marginBottom: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            Select a bin zone
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)',
            gap: '10px',
          }}>
            {ALL_BIN_PREFIXES.map(prefix => {
              const stats = getPrefixStats(prefix);
              const allAudited = stats.auditedCount === stats.slots && stats.slots > 0;
              const someAudited = stats.auditedCount > 0 && !allAudited;
              return (
                <div
                  key={prefix}
                  onClick={() => setBinZone(prefix)}
                  style={{
                    background: allAudited ? '#f0fdf4' : colors.cardBg,
                    border: `1px solid ${allAudited ? '#bbf7d0' : someAudited ? '#fde68a' : colors.border}`,
                    borderRadius: '6px',
                    padding: '16px 10px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    color: allAudited ? colors.greenDark : colors.navy,
                  }}>{prefix}</div>
                  <div style={{
                    fontSize: '0.65rem',
                    color: colors.textSecondary,
                    marginTop: '4px',
                  }}>{stats.slots} slots</div>
                  {stats.totalLots > 0 && (
                    <div style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: colors.navyLight,
                      marginTop: '2px',
                    }}>{stats.totalLots} lots</div>
                  )}
                  {stats.auditedCount > 0 && (
                    <div style={{
                      fontSize: '0.55rem',
                      color: colors.greenDark,
                      marginTop: '2px',
                    }}>
                      {allAudited ? '✓ all audited' : `✓ ${stats.auditedCount}/${stats.slots}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Individual bins/locations */}
      {!showingBinPicker && (
        <>
          {/* Back button for bin zones */}
          {activeZone === 'bins' && binZone && (
            <button
              onClick={() => setBinZone(null)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.navy,
                fontFamily: "'Outfit', sans-serif",
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                padding: '0 0 12px 0',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              ← All Bin Zones
            </button>
          )}

          <div style={{
            fontSize: '0.78rem',
            color: colors.textSecondary,
            marginBottom: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            {binZone ? `${binZone} Zone` : currentZone.label} · {binNames.length} locations
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)',
            gap: '8px',
          }}>
            {binNames.map(name => {
              const auditDate = auditedBins[name];
              const wasAudited = !!auditDate;
              const count = lotCounts[name] || 0;
              const density = getDensityColor(count);
              return (
                <div
                  key={name}
                  onClick={() => navigate(`/browse?bin=${name}`)}
                  style={{
                    background: density ? density.bg : colors.cardBg,
                    border: `1px solid ${density ? density.border : colors.border}`,
                    borderRadius: '6px',
                    padding: '14px 8px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    color: wasAudited ? colors.greenDark : colors.textPrimary,
                  }}>{name}</div>
                  {count > 0 && (
                    <div style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: density ? density.text : colors.textSecondary,
                      marginTop: '2px',
                    }}>{count} lots</div>
                  )}
                  {wasAudited && (
                    <div style={{
                      fontSize: '0.55rem',
                      color: colors.greenDark,
                      marginTop: '2px',
                    }}>✓ {formatDate(auditDate)}</div>
                  )}
                  {!wasAudited && count === 0 && (
                    <div style={{
                      fontSize: '0.6rem',
                      color: colors.textSecondary,
                      marginTop: '4px',
                    }}>—</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default Stockroom;